import type {
  InsertSlashCommandArgs,
  InsertSlashCommandResult,
  SlashCommand,
} from '../../types/editor/slash-commands.types';

/**
 * Filters slash commands based on a query string.
 *
 * Performs a case-insensitive substring match against both the command's
 * label and id. Returns all commands if the query is empty after trimming.
 *
 * @param commands - Array of slash commands to filter
 * @param query - Query string to match against command labels and ids
 * @returns Filtered array of commands matching the query
 *
 * @example
 * ```ts
 * const commands = [
 *   { id: 'status:1', label: 'In Progress', kind: 'status' },
 *   { id: 'status:2', label: 'Done', kind: 'status' }
 * ];
 * const filtered = filterSlashCommands(commands, 'prog');
 * // Result: [{ id: 'status:1', label: 'In Progress', kind: 'status' }]
 * ```
 */
export function filterSlashCommands(
  commands: SlashCommand[],
  query: string
): SlashCommand[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return commands;
  }

  return commands.filter((command) => {
    const label = command.label.toLowerCase();
    const id = command.id.toLowerCase();
    return label.includes(trimmed) || id.includes(trimmed);
  });
}

/**
 * Derives default insertion text from a slash command.
 *
 * For built-in kinds (status, step), generates markup in the form `[[kind:value]]`.
 * For unknown kinds, falls back to inserting the command label.
 *
 * @param command - The slash command to convert
 * @returns Markup or text representation of the command
 *
 * @example
 * ```ts
 * const statusCmd = { id: 'status:1', label: 'In Progress', kind: 'status', payload: { statusId: 1 } };
 * const text = getDefaultInsertionText(statusCmd);
 * // Result: "[[status:1]]"
 * ```
 */
const getDefaultInsertionText = (command: SlashCommand): string => {
  const statusId = command.payload?.statusId;
  if (command.kind === 'status' && typeof statusId === 'number') {
    return `[[status:${statusId}]]`;
  }

  const stepNumber = command.payload?.stepNumber;
  if (command.kind === 'step' && typeof stepNumber === 'number') {
    return `[[step:${stepNumber}]]`;
  }

  // Generic fallback: insert the label.
  return command.label;
};

/**
 * Inserts a slash command into editor text at the slash menu's opening position.
 *
 * Replaces the slash character and any typed query with the command's insertion text.
 * Uses a custom insertion mapper if provided, otherwise applies default logic.
 * Returns the updated text and new caret position for cursor restoration.
 *
 * @param args - Arguments including text, offsets, query, command, and optional mapper
 * @returns Updated text and new caret position
 *
 * @example
 * ```ts
 * const result = insertSlashCommand({
 *   text: 'Hello / world',
 *   startOffset: 7,
 *   query: '',
 *   command: { id: 'status:1', label: 'In Progress', kind: 'status', payload: { statusId: 1 } }
 * });
 * // Result: { nextText: 'Hello [[status:1]] world', caret: 19 }
 * ```
 */
export function insertSlashCommand(
  args: InsertSlashCommandArgs
): InsertSlashCommandResult {
  const { text, startOffset, query, command, getInsertionText } = args;

  const slashIndex = startOffset - 1;
  if (slashIndex < 0 || slashIndex > text.length) {
    const insertion = (getInsertionText ?? getDefaultInsertionText)(command);
    const nextText = `${text}${insertion}`;
    return { nextText, caret: nextText.length };
  }

  const expectedSlash = text[slashIndex];
  const safeSlashIndex = expectedSlash === '/' ? slashIndex : startOffset;

  const queryLength = query.length;
  const selectionStart = safeSlashIndex;
  const selectionEnd = Math.min(text.length, safeSlashIndex + 1 + queryLength);

  const before = text.slice(0, selectionStart);
  const after = text.slice(selectionEnd);
  const insertion = (getInsertionText ?? getDefaultInsertionText)(command);

  const nextText = `${before}${insertion}${after}`;
  const caret = before.length + insertion.length;

  return { nextText, caret };
}
